/**
 * F17 T17.11.5 — lista wzorów procesów w panelu admina.
 *
 * Wzorce: Admin scope (cookie, bez Bearer). Layout z <Layout>.
 * Akcje: utwórz nowy, edytuj blueprint, publikuj, usuń (ConfirmDialog).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GitBranch, Plus, Send, Trash2 } from 'lucide-react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Layout } from '@/components/Layout';
import { useToast } from '@/contexts/ToastContext';
import {
  deleteAdminProcessTemplate,
  listAdminProcessTemplates,
  type ProcessTemplateRecord,
  publishAdminProcessTemplate,
} from '@/lib/admin-process-templates/api';

import { CreateTemplateModal } from './CreateTemplateModal';

// ============================================================================
// Status badge
// ============================================================================

function StatusBadge({ isPublished }: { isPublished: boolean }) {
  const { t } = useTranslation();
  if (isPublished) {
    return (
      <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300">
        {t('admin.processTemplates.status.published')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
      {t('admin.processTemplates.status.draft')}
    </span>
  );
}

// ============================================================================
// Page component
// ============================================================================

export function AdminProcessTemplatesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ProcessTemplateRecord | null>(null);
  const [pendingPublish, setPendingPublish] = useState<ProcessTemplateRecord | null>(null);

  // ---- Query ----
  const listQuery = useQuery({
    queryKey: ['admin', 'process-templates'],
    queryFn: () => listAdminProcessTemplates({ limit: 100 }),
    staleTime: 30_000,
  });

  // ---- Delete mutation ----
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteAdminProcessTemplate(id),
    onSuccess: (result) => {
      setPendingDelete(null);
      if (result.ok) {
        toast.show({
          variant: 'success',
          title: t('admin.processTemplates.toast.deleteSuccess'),
        });
        void queryClient.invalidateQueries({ queryKey: ['admin', 'process-templates'] });
      } else {
        const codeMap: Record<string, string> = {
          ALREADY_DELETED: t('admin.processTemplates.errors.alreadyDeleted'),
          TEMPLATE_NOT_FOUND: t('admin.processTemplates.errors.notFound'),
        };
        toast.show({
          variant: 'error',
          title: t('admin.processTemplates.toast.deleteError'),
          description: codeMap[result.error] ?? result.error,
        });
      }
    },
    onError: () => {
      setPendingDelete(null);
      toast.show({
        variant: 'error',
        title: t('admin.processTemplates.toast.deleteError'),
      });
    },
  });

  // ---- Publish mutation ----
  const publishMutation = useMutation({
    mutationFn: (id: string) => publishAdminProcessTemplate(id),
    onSuccess: (result) => {
      setPendingPublish(null);
      if (result.ok) {
        toast.show({
          variant: 'success',
          title: t('admin.processTemplates.toast.publishSuccess'),
        });
        void queryClient.invalidateQueries({ queryKey: ['admin', 'process-templates'] });
      } else {
        const codeMap: Record<string, string> = {
          GRAPH_VALIDATION_FAILED: t('admin.processTemplates.errors.graphValidationFailed'),
          ALREADY_PUBLISHED: t('admin.processTemplates.errors.alreadyPublished'),
        };
        toast.show({
          variant: 'error',
          title: t('admin.processTemplates.toast.publishError'),
          description: codeMap[result.error] ?? result.error,
        });
      }
    },
    onError: () => {
      setPendingPublish(null);
      toast.show({
        variant: 'error',
        title: t('admin.processTemplates.toast.publishError'),
      });
    },
  });

  // ---- Render ----
  const templates = listQuery.data?.data ?? [];

  return (
    <Layout title={t('admin.processTemplates.title')}>
      <div className="mx-auto max-w-6xl px-6 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text-primary">
              {t('admin.processTemplates.title')}
            </h1>
            <p className="mt-1 text-sm text-text-secondary">
              {t('admin.processTemplates.subtitle')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="flex items-center gap-2 rounded-lg bg-accent-600 px-4 py-2 text-sm font-medium text-white hover:bg-accent-700"
            data-testid="create-template-btn"
          >
            <Plus className="h-4 w-4" />
            {t('admin.processTemplates.actions.create')}
          </button>
        </div>

        {/* Loading */}
        {listQuery.isLoading && (
          <div className="text-sm text-text-secondary" data-testid="templates-loading">
            {t('common.loading')}
          </div>
        )}

        {/* Error */}
        {listQuery.isError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800/50 dark:bg-red-900/20 dark:text-red-400">
            {t('admin.processTemplates.loadError')}
          </div>
        )}

        {/* Empty state */}
        {!listQuery.isLoading && !listQuery.isError && templates.length === 0 && (
          <div
            className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border-primary py-16 text-center"
            data-testid="templates-empty"
          >
            <GitBranch className="mb-3 h-10 w-10 text-text-secondary opacity-40" />
            <p className="text-sm font-medium text-text-primary">
              {t('admin.processTemplates.emptyTitle')}
            </p>
            <p className="mt-1 text-sm text-text-secondary">
              {t('admin.processTemplates.emptySubtitle')}
            </p>
          </div>
        )}

        {/* Table */}
        {templates.length > 0 && (
          <div className="overflow-hidden rounded-xl border border-border-primary bg-bg-primary">
            <table className="w-full text-sm" data-testid="templates-table">
              <thead>
                <tr className="border-b border-border-primary bg-bg-secondary text-xs font-medium text-text-secondary">
                  <th className="px-4 py-3 text-left">
                    {t('admin.processTemplates.columns.name')}
                  </th>
                  <th className="px-4 py-3 text-left">
                    {t('admin.processTemplates.columns.module')}
                  </th>
                  <th className="px-4 py-3 text-left">
                    {t('admin.processTemplates.columns.status')}
                  </th>
                  <th className="px-4 py-3 text-right">
                    {t('admin.processTemplates.columns.version')}
                  </th>
                  <th className="px-4 py-3 text-right">
                    {t('admin.processTemplates.columns.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-primary">
                {templates.map((tpl) => (
                  <tr
                    key={tpl.id}
                    className="hover:bg-bg-secondary"
                    data-testid={`template-row-${tpl.id}`}
                  >
                    <td className="px-4 py-3">
                      <span className="font-medium text-text-primary">{tpl.name}</span>
                      <span className="ml-2 font-mono text-xs text-text-secondary">{tpl.slug}</span>
                    </td>
                    <td className="px-4 py-3 text-text-secondary">{tpl.targetModuleCode}</td>
                    <td className="px-4 py-3">
                      <StatusBadge isPublished={tpl.isPublished} />
                    </td>
                    <td className="px-4 py-3 text-right text-text-secondary">v{tpl.version}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {/* Edytuj blueprint */}
                        <button
                          type="button"
                          onClick={() => navigate(`/process-templates/builder/${tpl.id}`)}
                          className="rounded-lg border border-border-primary bg-bg-primary px-3 py-1.5 text-xs font-medium text-text-primary hover:bg-bg-secondary"
                          data-testid={`edit-template-btn-${tpl.id}`}
                        >
                          {t('admin.processTemplates.actions.edit')}
                        </button>

                        {/* Publikuj (tylko draft) */}
                        {!tpl.isPublished && (
                          <button
                            type="button"
                            onClick={() => setPendingPublish(tpl)}
                            className="flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700"
                            data-testid={`publish-template-btn-${tpl.id}`}
                          >
                            <Send className="h-3 w-3" />
                            {t('admin.processTemplates.actions.publish')}
                          </button>
                        )}

                        {/* Usuń */}
                        <button
                          type="button"
                          onClick={() => setPendingDelete(tpl)}
                          className="flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 dark:border-red-800/50 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
                          data-testid={`delete-template-btn-${tpl.id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                          {t('admin.processTemplates.actions.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create modal */}
      <CreateTemplateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => {
          setCreateOpen(false);
          navigate(`/process-templates/builder/${id}`);
        }}
      />

      {/* Delete confirm */}
      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('admin.processTemplates.deleteDialog.title')}
        message={
          pendingDelete
            ? t('admin.processTemplates.deleteDialog.message', { name: pendingDelete.name })
            : ''
        }
        confirmLabel={t('common.delete')}
        variant="danger"
        onConfirm={() => {
          if (pendingDelete) {
            deleteMutation.mutate(pendingDelete.id);
          }
        }}
        onClose={() => setPendingDelete(null)}
      />

      {/* Publish confirm */}
      <ConfirmDialog
        open={pendingPublish !== null}
        title={t('admin.processTemplates.publishDialog.title')}
        message={
          pendingPublish
            ? t('admin.processTemplates.publishDialog.message', { name: pendingPublish.name })
            : ''
        }
        confirmLabel={t('admin.processTemplates.actions.publish')}
        variant="primary"
        onConfirm={() => {
          if (pendingPublish) {
            publishMutation.mutate(pendingPublish.id);
          }
        }}
        onClose={() => setPendingPublish(null)}
      />
    </Layout>
  );
}
